/**
 * Payment endpoints for the API contract.
 *
 * These endpoints handle subscription management across Stripe and RevenueCat.
 *
 * @see packages/core/api-contract/README.md for the full endpoint list
 */
import {
  HttpApiEndpoint,
  HttpApiGroup,
  HttpApiSchema,
  OpenApi,
} from 'effect/unstable/httpapi';
import { Effect, Schema } from 'effect';
import { UserAuthMiddleware } from './middleware';
import { BadRequestError, InternalServerError } from './errors';

// Import schemas from legacy client where available
import {
  ReferralSubscriptionView,
  RevenueCatSubscriptionView,
  StripeSubscriptionView,
} from './legacy-client.gen';

/**
 * The generated `StripeSubscriptionView` plus the one field the plan page
 * needs: the pricing-test arm the subscription was created under.
 *
 * Composed here rather than regenerated into `legacy-client.gen.ts`, which is
 * generated from the deployed legacy OpenAPI schema and cannot describe a
 * response that is not deployed yet. Existing clients ignore the additive
 * field; typed callers get it without a generated-file/deployment cycle.
 *
 * Only a recognized arm or `null` ever reaches the browser — the server
 * validates the Stripe metadata through `decodePricingArm` first, so this
 * is never a passthrough of arbitrary metadata. `null` means "not an
 * experiment subscription": every legacy, owed-time and unenrolled
 * subscription.
 *
 * The wire type is deliberately lenient (`string | null | undefined`) while the
 * server stays strict, because client and server deploy independently: a
 * required strict-literal field fails the WHOLE `GetSubscriptions` decode when
 * an older server omits the key, and a future arm name would fail it on older
 * clients. Consumers narrow with `decodePricingArm`, which treats anything
 * it does not recognize as "not an experiment subscription".
 *
 * Why the plan page needs it: the annual price a monthly subscriber may switch
 * *to* cannot be read off their own subscription, and the live feature flag is
 * the wrong source — it disappears when the experiment ends while the
 * grandfathered price is forever. The stamp on the subscription is durable.
 */
export const StripeSubscriptionViewWithPricingVariant =
  StripeSubscriptionView.pipe(
    Schema.fieldsAssign({
      pricing_variant: Schema.optional(Schema.NullOr(Schema.String)),
    }),
  );

/**
 * The pricing-test arm this account is quoted at RIGHT NOW, as resolved by the
 * API's Mode C ladder: the account's pinned row, else the request's
 * `lis_bucket` bucket, else the experiment's declared fallback arm.
 *
 * This is the paywall's only arm source. It cannot be a client-side feature-flag
 * read: the pin is a per-account database row, and the price a paywall displays
 * has to be the price the checkout endpoints will charge — which is this ladder,
 * evaluated on the same request. See
 * docs/projects/active/edge-experiments-account-pinned-bucketing.md §3.4.
 *
 * - `variant` is the arm whose PRICES and trial the surface renders. Always a
 *   live arm key (never an edge code — the API decodes at its own boundary).
 * - `enrolled` says whether this account is an experiment SUBJECT. False for
 *   the fallback and non-enrolled rungs, which are served a defined price
 *   without being randomized into it. Only an enrolled account may send
 *   `expected_variant`, be stamped into Stripe, or fire an exposure event —
 *   sending the arm for a non-subject would put unrandomized revenue in the
 *   experiment's results.
 *
 * Lenient wire type and an optional field for the same client/server deploy-skew
 * reason as `pricing_variant` above: an older server omits it and a newer arm
 * name must not fail the whole `GetSubscriptions` decode. Consumers narrow with
 * `decodePricingArm`.
 */
export const PricingArmView = Schema.Struct({
  variant: Schema.String,
  enrolled: Schema.Boolean,
});

/**
 * Local `GetGetSubscriptions200` composed around the extended Stripe view.
 * Field-for-field the generated shape otherwise, plus `pricing_arm`.
 */
const GetSubscriptionsResponse = Schema.Struct({
  referral_subscription: Schema.NullOr(ReferralSubscriptionView).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  revenue_cat_subscription: Schema.NullOr(RevenueCatSubscriptionView),
  stripe_subscription: Schema.NullOr(StripeSubscriptionViewWithPricingVariant),
  pricing_arm: Schema.optional(Schema.NullOr(PricingArmView)),
});

/**
 * GET /payment/subscriptions
 * Returns subscription status across all payment platforms (Stripe, RevenueCat, referrals).
 */
const getSubscriptionsEndpoint = HttpApiEndpoint.get(
  'GetSubscriptions',
  '/subscriptions',
  {
    success: GetSubscriptionsResponse,
    error: InternalServerError,
  },
)
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'Get user subscriptions')
  .annotate(
    OpenApi.Description,
    'Returns the current user subscription status across all payment platforms including Stripe, RevenueCat, and referral subscriptions, plus the pricing-test arm this account is currently quoted at.',
  );

export const paymentsGroup = HttpApiGroup.make('Payments')
  .add(getSubscriptionsEndpoint)
  .prefix('/payment');

// ============================================================================
// Money Endpoints (legacy DTO compat)
// ============================================================================

const PaymentDetailsResponse = Schema.Union([
  Schema.Struct({
    status: Schema.Literal('success'),
    nextPaymentDate: Schema.String,
  }),
  Schema.Struct({
    status: Schema.Literal('failed'),
  }),
]);

const getPaymentDetailsEndpoint = HttpApiEndpoint.get(
  'GetPaymentDetails',
  '/getPaymentDetails',
  {
    success: PaymentDetailsResponse,
    error: InternalServerError,
  },
)
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'Get payment details (legacy)')
  .annotate(
    OpenApi.Description,
    'Returns the next payment date for the user. Legacy DTO shape for web client compat.',
  );

export const moneyGroup = HttpApiGroup.make('Money')
  .add(getPaymentDetailsEndpoint)
  .prefix('/money');

// ============================================================================
// Stripe Endpoints
// ============================================================================

/**
 * POST /stripe/webhook
 * Stripe webhook for subscription lifecycle events. No auth — uses Stripe signature verification.
 */
const stripeWebhookEndpoint = HttpApiEndpoint.post(
  'StripeWebhook',
  '/webhook',
  {
    success: Schema.Void.pipe(HttpApiSchema.status(204)),
    error: [BadRequestError, InternalServerError],
  },
)

  .annotate(OpenApi.Summary, 'Stripe webhook')
  .annotate(
    OpenApi.Description,
    'Handles Stripe webhook events (customer.subscription.updated, invoice.paid). Verified via Stripe signature.',
  );

export const stripeGroup = HttpApiGroup.make('Stripe')
  .add(stripeWebhookEndpoint)
  .prefix('/stripe');

// ============================================================================
// Webhooks Group (RevenueCat)
// ============================================================================

/**
 * POST /webhooks/revenutCat
 * RevenueCat webhook for mobile subscription lifecycle events. No auth.
 * Note: the path contains a legacy typo ("revenut" instead of "revenue") that is
 * baked into RevenueCat's webhook configuration and cannot be changed.
 */
const revenueCatWebhookEndpoint = HttpApiEndpoint.post(
  'RevenueCatWebhook',
  '/revenutCat',
  {
    success: Schema.Void.pipe(HttpApiSchema.status(204)),
    error: InternalServerError,
  },
)

  .annotate(OpenApi.Summary, 'RevenueCat webhook')
  .annotate(
    OpenApi.Description,
    'Handles RevenueCat webhook events (INITIAL_PURCHASE, RENEWAL, CANCELLATION, PRODUCT_CHANGE, TRANSFER).',
  );

export const webhooksGroup = HttpApiGroup.make('Webhooks')
  .add(revenueCatWebhookEndpoint)
  .prefix('/webhooks');
